import { defineStore } from 'pinia'
import { convertDecitimeToKey } from '@/front/composables/TimeFormatter.js'
import { PlayerStore } from '@/front/stores/player_store.js'
import { DesignStore } from '@/front/stores/design_store.js'
import { PlaybackStore } from '@/front/stores/playback_store.js'
import { UserStore } from '@/front/stores/user_store.js'
import { VideoStore } from '@/front/stores/video_store.js'
import { AudioStore } from '@/front/stores/audio_store.js'
import { ClipStore } from '@/front/stores/clip_store.js'
import { ClickStore } from '@/front/stores/click_store.js'
import { focusAndSelect, focusAndHighlight } from '@/front/composables/UxComposer';

import { transformContent, numberGreaterThan, numberLessThan, equalsIgnoreCase, containsIgnoreCase, extractTemplateVariablesFromValue } from '@/front/composables/StringComposer';
import { doRedirect, doVariableEvaluation } from '@/front/composables/ClickComposer'
import { LinkValidator } from '@/front/composables/LinkValidator.js'
const { isValidURL } = LinkValidator()
import { VideoService } from '@/front/services'
import { InteractionService } from '@/front/services'
import { LeadCaptureStore } from '@/front/stores/lead_capture_store.js'
import { PaymentStore } from '@/front/stores/payment_store.js'
import { preloadManager } from '@/front/services/PreloadManager.js'

import { BrowserHelper } from '@/front/composables/BrowserHelper.js'
const { browserInfo } = BrowserHelper();
import { isNotEmpty } from '@/front/composables/GeneralHelper.js'


import { cloneDeep } from 'lodash';
import { nextTick } from 'vue';

export const AnnStore = defineStore('anns', {
  state: () => {
    return {
      active: new Map(), // Use Map instead of Array for active interactions
      activeQuestionQueue: [],
      originalQuestionQueue: [], // Holds the original state of the question queue for numbering and indexing
      // annById: new Map(),
      anns: [],
      bulkEditInteractions: [],
      bulkEditOriginalInteractions: [],
      directVisibilityMap: new Map(),
      doneInteractions: new Map(),
      editAnn: null, // holds the current state of the interaction being edited
      hoverAnn: null, // holds the current state of the interaction being hovered over (NOT MARKER HOVER)
      imageLoading: false,
      isBulkEditing: false,
      isEditing: false, // flag to track if we are editing an interaction
      lastReset: null,
      loading: true, // loading the annotations
      markerAnn: null, // holds the current state of the interaction being hovered over (MARKER HOVER)
      mustClickInteractions: [],
      ogAnn: null, // holds the original copy of the interaction
      originalAnns: [], // Holds the original state of the annotations for bulk editing reversion
      ownerMessage: null,
      ownerSwitchDialogVisible: false,
      ownerSwitchAnn: null,
      lastOwnerSwitchPromptTime: null,
      ownerSwitchTimeLeft: true,
      pausedInteraction: null, // holds the current state of the interaction being paused,
      saving: false, // saving an interaction
      generatedQuestions: [],
      questionGenerationId: null,
      questionGenerationStatus: null,
      questionGenerationProgress: 0,
      questionGenerationCompleted: 0,
      questionGenerationRequested: 0,
      showAudioSelector: false,
      showImageSelector: false,
      showVideoSelector: false,
      spotlightInteraction: null,
      timeCache: new Map(),
      timeMap: {}, // map of all interactions by time
      useGrid: false,
      cachedPlaybackStore: null,
      lastUpdateTime: null, // Stores time of last updateActive call,
      missedUpdates: [], // Array to track missed decitime updates
      isIntentionalSeek: false, // Flag to track when seeks are intentional
      seekTimeout: null, // Timeout to clear seek flag
      usesFontAwesome: false,
    }
  },

  actions: {

    startSpotlight(ann) {
      this.spotlightInteraction = ann;
    },

    endSpotlight() {
      this.spotlightInteraction = null;
    },
    // Track missed decitime updates for performance analysis
    trackMissedUpdate(currentTime, lastTime, reason = 'thread_blocked') {
      const missedUpdate = {
        timestamp: performance.now(),
        currentTime: currentTime,
        lastTime: lastTime,
        gap: currentTime - lastTime,
        reason: reason,
        seeking: this.isIntentionalSeek
      };
      
      this.missedUpdates.push(missedUpdate);
      
      // Keep only last 100 missed updates to prevent memory growth
      if (this.missedUpdates.length > 20) {
        this.missedUpdates.shift();
      }
      
      // Log missed update with details
      log.info(1,`🔴 MISSED UPDATE: ${currentTime}s (gap: ${(currentTime - lastTime).toFixed(1)}s, reason: ${reason}, seeking: ${this.isIntentionalSeek})`);
      
      // Process missed interactions to maintain state consistency
      this.processMissedTimeRange(lastTime, currentTime);
      
      return missedUpdate;
    },

    // Process all missed interactions in a time range
    processMissedTimeRange(startTime, endTime, includeStartTime = false) {
      const actualStartTime = includeStartTime ? startTime : Number(startTime + 0.1).toFixed(1);
      log.info(2,`🔄 Processing missed time range: ${actualStartTime}s → ${endTime}s`);
      
      const missedInteractions = this.findMissedInteractions(startTime, endTime, includeStartTime);
      
      if (missedInteractions.length === 0) {
        log.info(2,`📭 No missed interactions found in range`);
        return;
      }
      
      log.info(2,`📋 Found ${missedInteractions.length} missed interactions:`, missedInteractions.map(i => `${i.ann.type_of} ${i.action} at ${i.time}s`));
      
      // Categorize missed interactions by severity
      const categorized = this.categorizeMissedInteractions(missedInteractions, endTime);
      
      log.info(2,`📊 Categorized: ${categorized.critical.length} critical, ${categorized.important.length} important, ${categorized.lowImpact.length} low-impact`);
      
      // Handle critical interactions first (require seeking back)
      if (categorized.critical.length > 0) {
        log.info(2,`🚨 Processing ${categorized.critical.length} CRITICAL interactions`);
        this.handleCriticalMissedInteractions(categorized.critical, endTime);
      }
      
      // Handle non-critical interactions (show without seeking)
      if (categorized.important.length > 0) {
        log.info(2,`⚠️ Processing ${categorized.important.length} IMPORTANT interactions`);
        this.handleImportantMissedInteractions(categorized.important, endTime);
      }
      
      // Process low-impact interactions (apply immediately if still relevant)
      if (categorized.lowImpact.length > 0) {
        log.info(2,`ℹ️ Processing ${categorized.lowImpact.length} LOW-IMPACT interactions`);
        this.handleLowImpactMissedInteractions(categorized.lowImpact, endTime);
      }
    },

    // Find all interactions that should have been processed in the missed time range
    findMissedInteractions(startTime, endTime, includeStartTime = false) {
      const missedInteractions = [];
      const startKey = Math.floor(startTime * 10);
      const endKey = Math.floor(endTime * 10);
      
      // For thread blocking: exclude start time (already processed)
      // For simulation: include start time (simulating from scratch)
      const firstKey = includeStartTime ? startKey : startKey + 1;
      
      // Check each decitime in the missed range
      for (let key = firstKey; key <= endKey; key++) {
        const timeKey = key / 10;
        const timeMapEntry = this.timeMap[convertDecitimeToKey(timeKey)];
        
        if (timeMapEntry) {
          // Collect show interactions
          if (timeMapEntry.show) {
            timeMapEntry.show.forEach(ann => {
              missedInteractions.push({
                ann: ann,
                action: 'show',
                time: timeKey,
                type: 'show'
              });
            });
          }
          
          // Collect hide interactions  
          if (timeMapEntry.hide) {
            timeMapEntry.hide.forEach(ann => {
              missedInteractions.push({
                ann: ann,
                action: 'hide', 
                time: timeKey,
                type: 'hide'
              });
            });
          }
        }
      }
      
      return missedInteractions;
    },

    // Categorize missed interactions by their impact and recovery strategy
    categorizeMissedInteractions(missedInteractions, currentTime) {
      const critical = [];
      const important = [];
      const lowImpact = [];
      
      // First pass: identify critical interactions
      missedInteractions.forEach(interaction => {
        const { ann, action, time } = interaction;
        
        if (this.isCriticalInteraction(ann, action, time, currentTime)) {
          critical.push(interaction);
        }
      });
      
      // Check if there are critical must_click hide actions
      const criticalMustClickHides = critical.filter(i => 
        i.ann.must_click && i.action === 'hide' && !i.ann.clicked
      );
      const criticalMustClickAnnIds = new Set(criticalMustClickHides.map(i => i.ann.id));
      
      // Second pass: categorize remaining interactions
      missedInteractions.forEach(interaction => {
        const { ann, action, time } = interaction;
        
        // Skip if already marked as critical
        if (critical.includes(interaction)) {
          return;
        }
        
        // Skip must_click show actions if their hide action is critical (seeking will handle them)
        if (ann.must_click && action === 'show' && criticalMustClickAnnIds.has(ann.id)) {
          log.info(2,`🔄 Skipping must-click show for ${ann.type_of} - will be handled by critical hide seek`);
          lowImpact.push(interaction);
          return;
        }
        
        // Important: Show without seeking (visual elements)
        if (this.isImportantInteraction(ann, action, time, currentTime)) {
          important.push(interaction);
        }
        // Low impact: Apply immediately if still relevant
        else {
          lowImpact.push(interaction);
        }
      });
      
      return { critical, important, lowImpact };
    },

    // Check if interaction is critical (requires seeking back)
    isCriticalInteraction(ann, action, time, currentTime) {
      // Critical interaction types that must be seeked back to
      const criticalTypes = ['question', 'jump', 'switch', 'back', 'pause', 'redirect'];
      
      // Check for critical interaction types (both show and hide actions)
      if (criticalTypes.includes(ann.type_of)) {
        // For questions, only critical if not already done
        if (ann.type_of === 'question') {
          return !this.isDoneAt(time, ann.id);
        }
        // All other critical types are always critical
        return true;
      }
      
      // Any annotation with pause=true is critical (both show and hide)
      if (ann.pause) {
        return true;
      }
      
      // Must-click interactions: only HIDE action is critical (requires seeking back)
      if (ann.must_click && action === 'hide' && !ann.clicked) {
        log.info(2,`🚨 Must-click not clicked at hide time: ${ann.type_of} at ${time}s`);
        return true;
      }
      
      // Debug logging to help identify why basic buttons might be seeking
      if (ann.type_of === 'button' && !ann.pause && !ann.must_click) {
        log.info(2,`🔍 Basic button categorization: ${ann.type_of} ${action} at ${time}s, current: ${currentTime}s, critical: false`);
      }
      
      return false;
    },

    // Check if interaction is important (show without seeking)
    isImportantInteraction(ann, action, time, currentTime) {
      // Must-click show actions are important (show without seeking)
      // BUT only if there's no corresponding critical hide action that will seek back anyway
      if (ann.must_click && action === 'show') {
        log.info(2,`⚠️ Must-click show: ${ann.type_of} at ${time}s - show without seeking`);
        return true;
      }
      
      if (action === 'hide') {
        // Only hide if the interaction is currently active
        const isActive = this.active.has(ann.id);
        if (ann.type_of === 'button' && !ann.pause && !ann.must_click) {
          log.info(2,`🔍 Basic button hide check: active=${isActive}, important=${isActive}`);
        }
        return isActive;
      }
      
      // Show visual elements that should still be visible
      if (['hotspot', 'button', 'text', 'image'].includes(ann.type_of)) {
        // Only show if it should still be active at current time
        // AND we haven't passed its end time completely
        const shouldShow = time <= currentTime && currentTime <= ann.exact_finish;
        if (ann.type_of === 'button' && !ann.pause && !ann.must_click) {
          log.info(2,`🔍 Basic button show check: time=${time}, current=${currentTime}, finish=${ann.exact_finish}, important=${shouldShow}`);
        }
        return shouldShow;
      }
      
      // Navigation interactions (redirects, jumps) are important if recent
      if (['redirect', 'jump', 'switch'].includes(ann.type_of)) {
        return (currentTime - time) < 0.5; // Only if within 0.5s
      }
      
      return false;
    },

    // Mark the start of an intentional seek operation
    markIntentionalSeek() {
      this.isIntentionalSeek = true;
      
      // Clear any existing timeout
      if (this.seekTimeout) {
        clearTimeout(this.seekTimeout);
      }
      
      // Auto-clear the flag after 1 second to handle edge cases
      this.seekTimeout = setTimeout(() => {
        this.isIntentionalSeek = false;
        this.seekTimeout = null;
      }, 1000);
    },

    // Clear the intentional seek flag
    clearIntentionalSeek() {
      this.isIntentionalSeek = false;
      if (this.seekTimeout) {
        clearTimeout(this.seekTimeout);
        this.seekTimeout = null;
      }
    },

    // Get missed updates summary for debugging
    getMissedUpdatesReport() {
      const report = {
        total: this.missedUpdates.length,
        byReason: {},
        averageGap: 0,
        maxGap: 0,
        recentUpdates: this.missedUpdates.slice(-10)
      };
      
      let totalGap = 0;
      this.missedUpdates.forEach(update => {
        // Group by reason
        if (!report.byReason[update.reason]) {
          report.byReason[update.reason] = 0;
        }
        report.byReason[update.reason]++;
        
        // Calculate stats
        totalGap += update.gap;
        if (update.gap > report.maxGap) {
          report.maxGap = update.gap;
        }
      });
      
      if (this.missedUpdates.length > 0) {
        report.averageGap = totalGap / this.missedUpdates.length;
      }
      
      return report;
    },

    // Clear missed updates array
    clearMissedUpdates() {
      this.missedUpdates = [];
    },

    // Handle critical missed interactions (requires seeking back)
    handleCriticalMissedInteractions(criticalInteractions, currentTime) {
      log.info(1,`🚨 CRITICAL MISS: Found ${criticalInteractions.length} critical interactions, seeking back`);
      
      // Separate must_click hide interactions from other critical interactions
      const mustClickHideInteractions = criticalInteractions.filter(i => 
        i.ann.must_click && i.action === 'hide' && !i.ann.clicked
      );
      
      // For other critical interactions, only consider "show" actions - we don't care about hide
      const otherCriticalShowInteractions = criticalInteractions.filter(i => 
        !i.ann.must_click && i.action === 'show'
      );
      
      let seekTime;
      
      if (mustClickHideInteractions.length > 0) {
        // For must_click hide interactions, seek to the exact_finish time
        // doHide() will handle the pausing logic for unclicked must_click interactions
        seekTime = Math.min(...mustClickHideInteractions.map(i => i.ann.exact_finish));
        log.info(1,`🚨 Must-click hide recovery: seeking to exact_finish ${seekTime}s`);
        
        mustClickHideInteractions.forEach(interaction => {
          const { ann } = interaction;
          log.info(1,`🚨 Must-click hide: ${ann.type_of} exact_finish at ${ann.exact_finish}s`);
        });
      } else if (otherCriticalShowInteractions.length > 0) {
        // For other critical interactions, seek to the earliest exact_time (show actions only)
        seekTime = Math.min(...otherCriticalShowInteractions.map(i => i.ann.exact_time));
        log.info(1,`🚨 Critical show recovery: seeking to exact_time ${seekTime}s`);
        
        otherCriticalShowInteractions.forEach(interaction => {
          const { ann } = interaction;
          log.info(1,`🚨 Critical show: ${ann.type_of} at exact_time ${ann.exact_time}s (${ann.content || ann.question_prompt})`);
        });
      }
      
      if (seekTime !== undefined) {
        // Seek back and let resetActive handle showing the interactions properly
        const playerStore = PlayerStore();
        log.info(1,`🚨 Seeking back to ${seekTime}s to recover critical interactions`);
        playerStore.seek(seekTime);
      }
    },

    // Handle important missed interactions (show without seeking)
    handleImportantMissedInteractions(importantInteractions, currentTime) {
      log.info(2,`⚠️ IMPORTANT MISS: Recovering ${importantInteractions.length} interactions without seeking`);
      
      importantInteractions.forEach(interaction => {
        const { ann, action, time } = interaction;
        
        try {
          if (action === 'show') {
            // Only show if it should still be visible
            const shouldShow = time <= currentTime && currentTime <= ann.exact_finish;
            log.info(2,`⚠️ Show check: ${ann.type_of} time=${time}, current=${currentTime}, finish=${ann.exact_finish}, shouldShow=${shouldShow}`);
            if (shouldShow) {
              log.info(2,`⚠️ Late show: ${ann.type_of} at ${time}s`);
              this.doShow(ann);
            }
          } else if (action === 'hide') {
            // Hide if currently active
            if (this.active.has(ann.id)) {
              log.info(2,`⚠️ Late hide: ${ann.type_of} at ${time}s`);
              this.doHide(ann);
            }
          }
        } catch (error) {
          log.info(1,`❌ Error recovering interaction:`, error, ann);
        }
      });
    },

    // Handle low-impact missed interactions (apply immediately if relevant)
    handleLowImpactMissedInteractions(lowImpactInteractions, currentTime) {
      log.info(2,`ℹ️ LOW IMPACT MISS: Processing ${lowImpactInteractions.length} low-impact interactions`);
      
      lowImpactInteractions.forEach(interaction => {
        const { ann, action, time } = interaction;
        
        try {
          // Variable setters can be applied immediately
          if (ann.variable && ann.value) {
            log.info(2,`ℹ️ Late variable: ${ann.variable} = ${ann.value}`);
            const playbackStore = this.cachedPlaybackStore || PlaybackStore();
            playbackStore.setVariable(ann.variable, ann.value);
          }
          
          // Tracking interactions can be marked as done
          if (ann.click_action === 'track' && action === 'show') {
            log.info(2,`ℹ️ Late tracking: ${ann.type_of} at ${time}s`);
            this.markAsDone(time, ann.id);
          }
          
          // Hide actions for low-impact items
          if (action === 'hide' && this.active.has(ann.id)) {
            this.doHide(ann);
          }
        } catch (error) {
          log.info(1,`❌ Error processing low-impact interaction:`, error, ann);
        }
      });
    },
    addAnn(type_of, isDuplicate = false) {
      log.info(2,"🔎 AnnStore addAnn", type_of)
      const playerStore = PlayerStore()
      const clickStore = ClickStore()
      clickStore.resetAll()
      playerStore.started = true
      const videoStore = VideoStore()
      const finish = Math.min(Number((playerStore.decitime + 10).toFixed(1)), Number((Math.floor(playerStore.safeDuration) - 0.1).toFixed(1)))
      const font_size = Number((playerStore.videoDimensions.width / 33).toFixed(1))
      const ann = {
        type_of: type_of,
        exact_time: Math.min(playerStore.decitime, Math.floor(playerStore.safeDuration) - 0.1).toFixed(1),
        exact_finish: finish,
        time: 0,
        content: '',
        isActive: true,
        pos_left: 35,
        pos_top: 41.5,
        pos_width: 30,
        pos_height: 17,
        click_action: null,
        click_value: null,
        border_color: null,
        border_width: null,
        border_radius: 10,
        background_color: null,
        text_color: null,
        font_size: font_size,
        og_video_width: playerStore.videoDimensions.width,
        og_video_height: playerStore.videoDimensions.height,
        token: videoStore.video.token,
        transition: type_of == 'question' ? videoStore.video.design.question_transition : videoStore.video.design.interactions_transition
      }
      log.info(2,"🔎 AnnStore addAnn ann", ann)

      // For button, hotspot, etc set specific styles
      this.setSpecificStyles(ann)
      this.setDefaultValues(ann)
      this.anns.push(ann)
      this.setMap()
      this.editAnn = ann
      this.isEditing = true
      log.info(2,"🔎 focusAndHighlight", 'edit-ann-content')

      // For interactions that only show at one time, e.g. questions and navs, set the finish time to one second after the show time
      if (!this.allNeeds.needsHideTime) {
        this.editAnn.exact_finish = ann.exact_time + 0.1
      }

      // Bump time if starter user
      const userStore = UserStore()
      if (userStore.user.plan == 'starter' && playerStore.decitime > 0) {
        ann.exact_time = Math.floor(ann.exact_time);
        ann.exact_finish = Math.floor(ann.exact_finish);
        ann.time = Math.floor(ann.exact_time);
        ann.finish = Math.floor(ann.exact_finish);
        playerStore.seek(ann.exact_time)
      }

      if (ann.type_of == 'image' && !isDuplicate) {
        log.info(2,"🔎 AnnStore addAnn image no content", ann)
        this.showImageSelector = true
      } else if (ann.type_of == 'video' && !isDuplicate) {
        this.showVideoSelector = true
        log.info(2,"🔎 AnnStore addAnn video", ann)
      } else if (ann.type_of == 'audio' && !isDuplicate) {
        this.showAudioSelector = true
      } else if (ann.type_of == 'question') {
        const clickStore = ClickStore()
        clickStore.showQuestion(ann)
      } else if (ann.type_of == 'drawing') {
        const clickStore = ClickStore()
        clickStore.startDrawing()
      } else {
        setTimeout(() => {
          focusAndHighlight('edit-ann-content')
        }, 100)
      }
    },
    setDefaultValues(ann) {
      const designStore = DesignStore()
      const playerStore = PlayerStore()
      ann.spotlight_mode = false
      ann.force_lead_capture = false
      ann.font_size = Math.round(playerStore.videoDimensions.width / 40)
      switch (ann.type_of) {
        case 'button':
          ann.click_action = 'track'
          ann.border_radius = designStore.design.button_border_radius
          break;
        case 'hotspot':
          ann.click_action = 'link'
          ann.hotspot_show_ring = true
          ann.hotspot_ring_width = 30
          ann.hotspot_alignment = 1
          ann.hotspot_radius = 3
          ann.hotspot_hover = true
          ann.show_caption = true
          break;
        case 'text':
          ann.click_action = 'track'
          ann.border_radius = designStore.design.comment_border_radius
          break;
        case 'image':
          ann.click_action = 'track'
          ann.show_caption = true
          break;
        case 'jump':
          ann.click_action = 'jump'
          ann.click_value = 0
          break;
        case 'redirect':
          ann.click_action = 'redirect'
          break;
        case 'drawing':
          ann.timer = 10
          break;
        case 'pause':
          ann.click_action = 'pause'
          ann.pause = true
          ann.timer = 10
          break;
        case 'question':
          ann.question_type = 'free'
          ann.question_prompt = ''
          ann.pos_top = designStore.design.question_top
          ann.pos_left = designStore.design.question_left
          ann.pos_width = designStore.design.question_width
          ann.pos_height = designStore.design.question_height
          ann.background_color = designStore.design.question_background_color
          ann.text_color = designStore.design.prompt_color
          break;
      }
    },

    updateAllConditionalStatuses(key = null) {
      this.cachedPlaybackStore = this.cachedPlaybackStore || PlaybackStore()
      let perfStart = performance.now()
      let conditionalAnns = key ? this.anns.filter(ann => 
        ann.is_conditional && 
        (ann.conditional_variable && ann.conditional_variable.toUpperCase() === key.toUpperCase() || 
        ann.conditional_variable_2 && ann.conditional_variable_2.toString().toUpperCase() === key.toUpperCase())
      ) : this.anns.filter(ann => ann.is_conditional)
      log.info(3,"🔎 AnnStore updateAllConditionalStatuses " + (key ? key.toUpperCase() : 'ALL'), conditionalAnns.length + " of " + this.anns.length)
      // Only process conditionals that use the changed variable
      conditionalAnns.forEach(ann => {
        this.setConditionalStatus(ann)
      })
      let perfEnd = performance.now()
      // log.info(3,"🔎 AnnStore updateAllConditionalStatuses finished in " + (perfEnd - perfStart).toFixed(2) + "ms")
    },

    getConditionalResult(variable, assertion, value) {
      // log.info(2,"🔎 AnnStore getConditionalResult", variable, assertion, value)
      const playbackStore = this.cachedPlaybackStore
      const variableValue = playbackStore.getVariable(variable)
      // VALUE is already lowercased via conditional_variable_lower
      let result = true
      switch (assertion) {
        case 'equals':
          result = equalsIgnoreCase(variableValue, value)
          break;
        case 'notequals':
          result = !equalsIgnoreCase(variableValue, value)
          break;
        case 'greaterthan':
          result = numberGreaterThan(variableValue, value)
          break;
        case 'lessthan':
          result = numberLessThan(variableValue, value)
          break;
        case 'exists':
          result = !!variableValue
          break;
        case 'notexists':
          result = !variableValue
          break;
        case 'contains':
          result = containsIgnoreCase(variableValue, value)
          break;
        case 'notcontains':
          result = !containsIgnoreCase(variableValue, value)
          break;
        case 'regex':
          result = variableValue.match(value)
          break;
      }
      return result
    },
    setConditionalStatus(ann) {
      
      const playbackStore = this.cachedPlaybackStore || PlaybackStore()
      this.cachedPlaybackStore = playbackStore
      if (playbackStore.manager || ann.hidden) {
        log.info(2,"🔎 AnnStore setConditionalStatus hidden or manager")
        return
      }
      
      let oldStatus = ann.visible
      
      if (ann.is_conditional) {
        const result1 = this.getConditionalResult(ann.conditional_variable_lower || ann.conditional_variable, ann.conditional_assertion, ann.conditional_value)
        
        if (ann.is_conditional_2) {
          const result2 = this.getConditionalResult(ann.conditional_variable_2_lower || ann.conditional_variable_2, ann.conditional_assertion_2, ann.conditional_value_2)
          
          // 0 = AND, 1 = OR
          let visible = ann.conditional_operator == 0 ? (result1 && result2) : (result1 || result2)
          ann.visible = ann.conditional_show ? visible : !visible
        } else {
          // Only one conditional
          ann.visible = ann.conditional_show ? result1 : !result1
        }
        if (oldStatus != ann.visible && playbackStore.started) {
          log.info(2,"🔎 AnnStore conditional status changed", ann.type_of, ann.content || ann.question_prompt, oldStatus + " --> " + ann.visible)
        }
                
        if (ann.type_of == 'question' && oldStatus != ann.visible) {
          log.info(2,"🔎 AnnStore question status changed", ann.type_of, ann.content || ann.question_prompt, oldStatus + " --> " + ann.visible)
          if (!ann.visible && this.originalQuestionQueue.includes(ann.id)) {
            log.info(2,"🔎 AnnStore question now hidden, remove from queue", ann.type_of, ann.content || ann.question_prompt, oldStatus + " --> " + ann.visible)
            this.originalQuestionQueue.splice(this.originalQuestionQueue.indexOf(ann.id), 1)
          } else if (ann.type_of == 'question' && ann.visible && !this.originalQuestionQueue.includes(ann.id)) {
            log.info(2,"🔎 AnnStore question now visible, add to queue", ann.type_of, ann.content || ann.question_prompt, oldStatus + " --> " + ann.visible)
            // If now visible and not in the originalQuestionQueue, add it to the originalQuestionQueue at the index that it is in the activeQuestionQueue
            let index = this.activeQuestionQueue.indexOf(ann.id)
            // Push the ID of the ann to the originalQuestionQueue at the index that it is in the activeQuestionQueue
            this.originalQuestionQueue.splice(index, 0, ann.id)
          }
        }
      }
      
    },
    setSpecificStyles(ann) {
      const playerStore = PlayerStore()
      const videoStore = VideoStore()
      if (ann.type_of === 'button') {
        ann.pos_left = 40
        ann.pos_top = 41.5
        ann.pos_width = 25
        ann.pos_height = 12.5
        ann.background_color = videoStore.video.design.button_background_color
        ann.text_color = videoStore.video.design.button_text_color
        ann.border_color = videoStore.video.design.button_border_color
      } else if (ann.type_of === 'hotspot') {
        ann.background_color
        ann.pos_left = 40
        ann.pos_top = 40
        ann.pos_width = 25
        ann.pos_height = 25
        ann.background_color = videoStore.video.design.color
        ann.border_color = videoStore.video.design.color
        ann.border_radius = videoStore.video.design.border_radius
      } else if (ann.type_of === 'text') {
        ann.pos_left = 40
        ann.pos_top = 40
        ann.pos_width = 20
        ann.pos_height = 10
        ann.text_align = 'left'
        ann.content = ''
        ann.text_color = videoStore.video.design.comment_text_color
        ann.font_size = playerStore.videoDimensions.width / 50
        log.info(2,"Set Text Color", ann.color)
        ann.background_color = videoStore.video.design.comment_background_color
        ann.border_radius = videoStore.video.design.comment_border_radius
        focusAndSelect('edit-ann-content')
      } else if (ann.type_of === 'image') {
        ann.pos_left = 40
        ann.pos_top = 40
        ann.pos_width = 25
        ann.pos_height = 25
      }
    },
    async setEditingImageAspectRatio() {
      return
      if (this.editAnn.type_of === 'image' && this.editAnn.content) {
        return new Promise((resolve, reject) => {
          const img = new Image()

          img.onload = () => {
            // Get the video and layer dimensions
            const playerStore = PlayerStore()
            const videoWidth = playerStore.videoDimensions.width
            const videoHeight = playerStore.videoDimensions.height
            const layerWidth = playerStore.layerDimensions.width
            const layerHeight = playerStore.layerDimensions.height

            // Calculate aspect ratio
            const aspectRatio = img.width / img.height

            if (!this.editAnn.id) {
              // For new images, calculate dimensions and center
              const maxWidthPx = layerWidth * 0.3
              const maxHeightPx = layerHeight * 0.3

              // Determine dimensions that maintain aspect ratio within our max bounds
              let targetWidthPx, targetHeightPx
              if (maxWidthPx / aspectRatio <= maxHeightPx) {
                // Width is the limiting factor
                targetWidthPx = maxWidthPx
                targetHeightPx = maxWidthPx / aspectRatio
              } else {
                // Height is the limiting factor
                targetHeightPx = maxHeightPx
                targetWidthPx = maxHeightPx * aspectRatio
              }

              // Convert pixel values to percentages of video dimensions
              const targetWidth = (targetWidthPx / layerWidth) * 100
              const targetHeight = (targetHeightPx / layerHeight) * 100

              // Set dimensions and center the image
              this.editAnn.pos_width = targetWidth
              this.editAnn.pos_height = targetHeight
              this.editAnn.pos_left = (100 - targetWidth) / 2
              this.editAnn.pos_top = (100 - targetHeight) / 2
            }

            // Always set these properties regardless of new or existing
            this.editAnn.aspect_ratio = aspectRatio
            this.editAnn.original_width = img.width
            this.editAnn.original_height = img.height
            this.editAnn.og_video_width = videoWidth
            this.editAnn.og_video_height = videoHeight

            resolve({
              aspectRatio,
              width: this.editAnn.pos_width,
              height: this.editAnn.pos_height
            })
          }

          img.onerror = () => {
            reject(new Error('Failed to load image'))
          }

          img.src = this.editAnn.content
        })
      }
      return Promise.resolve(null)
    },
    startEdit(id) {
      log.info(2,"AnnStore startEdit "+ id)

      const clickStore = ClickStore()
      const ann = this.anns.find(ann => ann.id === id)
      if (!ann) {
        log.info(2,"AnnStore startEdit ann not found", id)
        return
      }
      log.info(2,"🔎 AnnStore startEdit ann", ann)
      clickStore.resetAll()
      this.markAsDone(ann.exact_time, ann.id)
      this.ogAnn = cloneDeep(ann);
      log.info(2,"ogAnn", this.ogAnn)
      this.editAnn = ann
      if (ann.type_of === 'image') {
        this.setEditingImageAspectRatio()
      } else if (ann.type_of === 'drawing') {
        clickStore.showDrawing(ann, false)
      }
      this.isEditing = true
      this.hoverAnn = null
      const playerStore = PlayerStore()
      playerStore.started = true
      log.info(2,"go to time", this.editAnn.exact_time)
      let isActive = this.active.has(ann.id)
      if (!isActive) {
        playerStore.seek(this.editAnn.exact_time)
        this.markAsDone(this.editAnn.exact_time, ann.id)
      }


      if (ann.type_of == 'video' && ann.content) {
        ClipStore().streamById(ann.content)
      } else if (ann.type_of == 'audio' && ann.content) {
        AudioStore().streamById(ann.content)
      } else if (ann.type_of == 'question') {
        const clickStore = ClickStore()
        clickStore.showQuestion(ann)
      }
    },

    startCopy(id, another = false) {
      // TOOD handle questions
      log.info(2,"AnnStore startCopy", id)
      const ann = this.anns.find(ann => ann.id === id)
      if (!ann) {
        log.info(2,"AnnStore startCopy ann not found", id)
        return
      }
      this.addAnn(ann.type_of, true)
      // Copy all attributes except ID from the source annotation
      Object.keys(ann).forEach(key => {
        if (key !== 'id' && key !== 'created_at' && key !== 'updated_at') {
          this.editAnn[key] = ann[key]
        }
      })
      if (another) {
        this.editAnn.content = this.editAnn.type_of == 'image' ? this.editAnn.content : ''
        this.editAnn.click_value = ''
        this.editAnn.pos_top = ann.pos_top + 5
        this.editAnn.pos_left = ann.pos_left + 5
      } else if (ann.type_of == 'video') {
        const clipStore = ClipStore()
        clipStore.streamById(ann.content).then((res) => {
          log.info(2,"🔎 AnnStore startCopy video", res)
        })
      } else if (ann.type_of == 'audio') {
        const audioStore = AudioStore()
        const clickStore = ClickStore()
        audioStore.streamById(ann.content).then((res) => {
          log.info(2,"🔎 AnnStore startCopy audio", res)
          clickStore.startAudio()
        })
      }
    },

    resetQuestionGenerationState() {
      this.questionGenerationId = null
      this.questionGenerationStatus = null
      this.questionGenerationProgress = 0
      this.questionGenerationCompleted = 0
      this.questionGenerationRequested = 0
    },

    async generateQuestions(count) {
      log.info(2,"💦 AnnStore generateQuestions", count)
      const videoStore = VideoStore()
      this.generatedQuestions = []
      this.resetQuestionGenerationState()

      const startResponse = await this.Api.post(`/annotations/${videoStore.video.id}/question_generation`, { count: count })
      const startData = startResponse.data || {}
      log.info(2,"🔎 AnnStore generateQuestions start response", startData)

      this.questionGenerationId = startData.generation_id || null
      this.questionGenerationStatus = startData.status || null
      this.questionGenerationProgress = startData.progress_percent || 0
      this.questionGenerationCompleted = startData.completed_count || 0
      this.questionGenerationRequested = startData.requested_count || Number(count) || 0

      if (startData.status === 'completed') {
        this.generatedQuestions = startData.questions || []
        return startData
      }

      if (!this.questionGenerationId) {
        throw { response: { data: { error: 'Question generation did not return a generation id.' } } }
      }

      return this.pollGeneratedQuestions(videoStore.video.id, this.questionGenerationId, startData.poll_after_ms || 2000)
    },

    async pollGeneratedQuestions(videoId, generationId, pollAfterMs = 2000) {
      const maxAttempts = 360
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const response = await this.Api.get(`/annotations/${videoId}/question_generation/${generationId}`)
        const payload = response.data || {}
        this.questionGenerationStatus = payload.status || this.questionGenerationStatus
        this.questionGenerationProgress = payload.progress_percent || this.questionGenerationProgress
        this.questionGenerationCompleted = payload.completed_count || this.questionGenerationCompleted
        this.questionGenerationRequested = payload.requested_count || this.questionGenerationRequested

        if (payload.status === 'completed') {
          this.generatedQuestions = payload.questions || []
          return payload
        }

        if (payload.status === 'failed') {
          throw { response: { data: payload } }
        }

        await new Promise((resolve) => setTimeout(resolve, pollAfterMs))
      }

      throw { response: { data: { error: 'Question generation timed out while waiting for background processing.' } } }
    },

    saveGeneratedQuestions() {
      log.info(2,"💦 AnnStore saveGeneratedQuestions", this.generatedQuestions)
      const videoStore = VideoStore()
      return this.Api.post(`/annotations/${videoStore.video.id}/save_generated_questions`, { questions: this.generatedQuestions }).then((res) => {
        log.info(2,"🔎 AnnStore saveQuestions response", res)
      })
    },

    addBulkEditInteraction(id) {
      log.info(2,"💦 AnnStore addBulkEditInteraction", id)
      this.bulkEditOriginalInteractions.push(cloneDeep(this.anns.find(ann => ann.id === id)))
      this.bulkEditInteractions.push(id)
    },

    removeBulkEditInteraction(ann_id) {
      log.info(2,"💦 AnnStore removeBulkEditInteraction", ann_id)
      let original = this.bulkEditOriginalInteractions.find(ann => ann.id === ann_id)
      log.info(2,"💦 AnnStore removeBulkEditInteraction original", original)
      let removedAnn = this.anns.find(ann => ann.id === ann_id)
      Object.assign(removedAnn, original)
      log.info(2,"💦 AnnStore removeBulkEditInteraction anns", this.anns)
      this.bulkEditInteractions = this.bulkEditInteractions.filter(id => id !== ann_id)
      log.info(2,"💦 AnnStore removeBulkEditInteraction bulkEditInteractions", this.bulkEditInteractions)
      this.bulkEditOriginalInteractions = this.bulkEditOriginalInteractions.filter(ann => ann.id !== ann_id)
    },
    startBulkEdit(ids) {
      const playerStore = PlayerStore()
      const clickStore = ClickStore()
      this.resetBulkEdit()
      clickStore.resetAll()
      playerStore.started = true
      playerStore.seek(this.anns.find(ann => ann.id === ids[0]).exact_time)
      playerStore.pause("AnnStore startBulkEdit")
      ids.forEach(id => {
        this.addBulkEditInteraction(id)
      })
      this.isBulkEditing = true
    },

    resetBulkEdit() {
      this.bulkEditInteractions = []
      this.bulkEditOriginalInteractions = []
    },


    async doBulkDestroy(ids) {
      log.info(2,"💦 AnnStore doBulkDestroy", ids)
      return this.Api.post(`/annotations/bulk_destroy`, { ids: ids }).then(() => {
        log.info(2,"Bulk Destroy Finished")
        this.stopBulkEdit()
      })
    },

    async doBulkEdit(ids, attribute, value) {
      const clickStore = ClickStore()
      log.info(2,"💦 AnnStore doBulkEdit", ids, attribute, value)
      return this.Api.post(`/annotations/bulk_edit`, { ids: ids, attribute: attribute, value: value }).then(() => {
        log.info(2,"Bulk Edit Finished")
        this.index().then(() => {
          this.setMap()
          clickStore.resetAll()
          this.startBulkEdit(ids)
        })
      })
    },


    async stopBulkEdit() {
      if (!this.isBulkEditing) {
       return
      }
      const clickStore = ClickStore()
      log.info(2,"💦 AnnStore stopBulkEdit")
      this.index().then(() => {
        this.isBulkEditing = false
        this.bulkEditInteractions = []
        this.setMap()
        clickStore.resetAll()
      })
    },

    async saveAndAddAnother(ann) {
      log.info(2,"💦 AnnStore saveAndAddAnother", ann)
      this.saveAnn(ann).then((annotation) => {
        log.info(2,"Saved ann, now starting copy", annotation)
        this.startCopy(annotation.id, true)
        this.editAnn.question_prompt = ''
      })
    },

    async saveAnn(ann) {
      this.saving = true

      log.info(2,"💦 AnnStore saveAnn", ann.id, "Saving is now " + this.saving)
      // if (ann.pause && ann.exact_time == 0) {
      //   ann.exact_time = 0.1
      // }
      if (ann.exact_time) {
        ann.time = Math.floor(ann.exact_time)
        ann.finish = Math.floor(ann.exact_finish)
      }

      if (ann.id) {
        return this.updateAnn(ann)
      } else {
        return this.createAnn(ann)
      }
    },
    findAnnotationById(id) {
      return this.anns.find(ann => ann.id === id)
    },
    async createAnn(ann) {
      const playerStore = PlayerStore()
      const userStore = UserStore()
      log.info(2,"💦 AnnStore createAnn", ann)
      return this.Api.post(`/annotations`, ann).then(response => {
        log.info(2,"💦 AnnStore createAnn response", response)
        this.stopEditing()
        this.anns.push(response.data.annotation)
        this.setMap()
        if (userStore.user?.org_owner && userStore.organization?.plan == 'free') {
          userStore.refreshActivation()
        }
        playerStore.seek(response.data.annotation.exact_time)
        return response.data.annotation
      }).catch(error => {
        log.info(2,"💦 AnnStore updateAnn error", error)
      }).finally(() => {
        this.saving = false
      })
    },
    async updateAnn(ann) {
      const playerStore = PlayerStore()
      const clickStore = ClickStore()
      log.info(2,"💦 AnnStore updateAnn", ann)
      return this.Api.put(`/annotations/${ann.id}`, ann).then(response => {
        log.info(2,"💦 AnnStore updateAnn response", response)
        const index = this.anns.findIndex(a => a.id === ann.id)
        if (index !== -1) {
          this.anns[index] = response.data.annotation
        }
        clickStore.resetAll()
        this.setMap()
        this.resetActive(null, "updateAnn")
        this.clearEditAnn()
        playerStore.seek(response.data.annotation.exact_time)
        return response.data.annotation

        // this.setMap()
        log.info(2,"Seek to ", response.data.annotation.exact_time)
      }).catch(error => {
        log.info(2,"💦 AnnStore updateAnn error", error)
      }).finally(() => {
        this.saving = false
      })
    },

    async deleteAnn(id) {
      const clickStore = ClickStore()
      return this.Api.destroy(`/annotations/${id}`).then(response => {
        log.info(2,"💦 AnnStore deleteAnn response", response)
        this.anns = this.anns.filter(item => item.id !== id);
        this.setMap()
        this.resetActive(null, "deleteAnn")
        clickStore.resetAll()
      })
    },

    stopEditing() {
      log.info(2,"💦 AnnStore stopEditing", this.editAnn)
      var ct = 0
      let lastId = null
      if (this.editAnn) {
        lastId = this.editAnn.id
        ct = this.editAnn.exact_time
        if (this.editAnn.id) {
          const index = this.anns.findIndex(i => i.id === this.editAnn.id);
          if (index !== -1) {
            Object.assign(this.anns[index], this.ogAnn);
            this.editAnn = null; // Clear editing state
            this.ogAnn = null; // Clear original interaction copy
          }
        }
      }
      this.clearEditAnn()
      const clickStore = ClickStore()
      clickStore.resetAll()
      // this.stopEditing()
      this.anns = this.anns.filter(item => item.id !== undefined && item.id !== null && !!item.id);
      this.resetActive(ct, "stopEditing")
      this.setMap()
      if (lastId) {
        setTimeout(() => {
          const element = document.getElementById('ann-card-' + lastId);
          const container = document.getElementById('ann-list')
          if (element && container) {
          container.scrollTo({
                top: element.offsetTop - container.clientHeight / 2 + 100,
            });
          }
        }, 50)
      }
    },

    clearEditAnn() {
      this.isEditing = false
      this.editAnn = null
      this.ogAnn = null
    },

    markAsDone(decitime, interactionId) {
      // log.info(2,"💟 AnnStore markAsDone", decitime, interactionId)
      if (!this.doneInteractions.has(decitime)) {
        this.doneInteractions.set(decitime, new Set())
      }
      this.doneInteractions.get(decitime).add(interactionId)
    },
    isDoneAt(decitime, interactionId) {
      // log.info(2,"💟 AnnStore isDoneAt", decitime, interactionId, this.doneInteractions.get(decitime)?.has(interactionId) || false)
      return this.doneInteractions.get(decitime)?.has(interactionId) || false
    },
    clearDoneAt(decitime) {
      // log.info(2,"💟 AnnStore clearDoneAt", this.doneInteractions.get(decitime)?.length)
      this.doneInteractions.delete(decitime)
    },


    async index(path) {
      const playbackStore = PlaybackStore()
      const videoStore = VideoStore()
      this.resetState()
      this.loading = true
      let indexPath = path ? path : videoStore.video.token
      log.info(2, "💟 Index", path)

      
      // Check for preloaded annotations data
      if (window.__PRELOADED_VIDEO_DATA__ && !path && !playbackStore.manager) {
        log.info(2,"🚀 Checking for preloaded annotations");
        try {
          const preloadedData = typeof window.__PRELOADED_VIDEO_DATA__ === 'string' 
            ? JSON.parse(window.__PRELOADED_VIDEO_DATA__) 
            : window.__PRELOADED_VIDEO_DATA__;
          
          if (preloadedData.annotations) {
            log.info(2,"✅ Using preloaded annotations data");
            return this.handlePreloadedAnnotations(preloadedData);
          } else {
            log.info(2,"❌ No annotations found in preloaded data");
          }
        } catch (error) {
          log.info(1,"❌ Error parsing preloaded annotations:", error);
          // Fall through to API call
        }
      }
      
      // Fallback to API call
      log.info(2,"📡 Fetching annotations via API");
      return this.Api.get(`/annotations/${indexPath}`).then(response => {
        // log.info(2,"AnnStore index response ", response)
        this.pagination = response.data.pagination;
        if (playbackStore.manager) {
          // log.info(2,"AnnStore index response for manager ", response)
          this.anns = response.data.annotations
        } else if (browserInfo.isMobile) {
          this.anns = response.data.annotations.filter(ann => !ann.hide_mobile)
        } else {
          this.anns = response.data.annotations.filter(ann => !ann.hide_desktop)
        }
        // Optimize sorting by using a single comparison function
        this.anns.sort((a, b) => {
          const timeDiff = a.exact_time - b.exact_time
          if (timeDiff !== 0) return timeDiff
          
          // Only compare created_at if times are equal
          return a.created_at_integer - b.created_at_integer
        })
        this.originalAnns = cloneDeep(this.anns)

        // Only substitute variables if we are not managing the video
        if (!playbackStore.manager) {
          // Personalize and do conditional
          this.handleVariableChange()
        }
        this.setMap()
        this.loading = false
        return response.data.annotations
      })
    },
    
    // Handle preloaded annotations data
    async handlePreloadedAnnotations(data) {
      log.info(2,"🎯 Processing preloaded annotations", data);
      const playbackStore = PlaybackStore();
      
      try {
        // Extract annotations array from the data structure
        const annotationsArray = data.annotations || [];
        
        // Set pagination if available
        this.pagination = data.pagination || null;
        
        // Filter annotations based on device type
        if (playbackStore.manager) {
          this.anns = annotationsArray;
        } else if (browserInfo.isMobile) {
          this.anns = annotationsArray.filter(ann => !ann.hide_mobile);
        } else {
          this.anns = annotationsArray.filter(ann => !ann.hide_desktop);
        }
        
        // Sort annotations
        this.anns.sort((a, b) => {
          const timeDiff = a.exact_time - b.exact_time;
          if (timeDiff !== 0) return timeDiff;
          return a.created_at_integer - b.created_at_integer;
        });
        
        this.originalAnns = cloneDeep(this.anns);
        
        // Personalize if not managing
        if (!playbackStore.manager) {
          this.handleVariableChange();
        }
        
        this.setMap();
        this.loading = false;
        
        log.info(2,"✅ Preloaded annotations processed:", this.anns.length);
        return this.anns;
      } catch (error) {
        log.info(1,"❌ Error processing preloaded annotations:", error);
        this.loading = false;
        // Fallback to API call
        return this.index();
      }
    },
    
    personalizeInteraction(ann) {
      const playbackStore = PlaybackStore()
      if (playbackStore.manager) {
        return
      }
      ann.transformed_content = transformContent(ann.content, playbackStore.variables)
      ann.transformed_question_prompt = transformContent(ann.question_prompt, playbackStore.variables)
      ann.transformed_click_value = transformContent(ann.click_value, playbackStore.variables)
      ann.transformed_caption = transformContent(ann.caption, playbackStore.variables)
      if (!ann.question_answers) {
        return
      }
      ann.transformed_question_answers = ann.question_answers.map(answer => {
        answer.text = transformContent(answer.text, playbackStore.variables)
        const personalizableActions = ['link', 'message', 'image']
        if (personalizableActions.includes(answer.action) || personalizableActions.includes(answer.click_action)) {
          // log.info(2,"Original answer", answer)
          answer.value = transformContent(answer.value, playbackStore.variables)
          answer.click_value = transformContent(answer.click_value, playbackStore.variables)
          // log.info(2,"Personalized answer", answer)
        }
        return answer
      })
      
    },
    handleVariableChange(key=null) {
      log.info(2,"💟  AnnStore handleVariableChange", key)
      const playbackStore = PlaybackStore()
      this.updateAllConditionalStatuses(key)
      this.personalizeAllInteractions()
    },
    personalizeAllInteractions() {
      log.info(2,"💟  AnnStore personalizeAllInteractions: " + this.dynamicAnns.length + " of " + this.anns.length)
      this.dynamicAnns.forEach(ann => {
        this.personalizeInteraction(ann)
      })
    },
    // For re-ordering questions
    async moveAnnotation(id, direction) {
      await this.Api.put(`/annotations/${id}/move`, { direction })
      this.index()
    },
    // ssssss
    async setMap() {
      const totalStart = performance.now();
      this.lastReset = null
      const playerStore = PlayerStore();
      
      // Create direct visibility map for O(1) lookup
      this.directVisibilityMap = new Map();
      
      // Create annotation index for fast lookups by ID
      // this.annById = new Map();
      
      // Pre-calculate conditional statuses in a single pass
      this.mainAnns.forEach(ann => {
        // this.setConditionalStatus(ann)
        this.personalizeInteraction(ann)
        // this.setConditionalStatus(ann)
        // Create ID lookup index
        // this.annById.set(ann.id, ann)
      });
      
      // Build traditional timeMap for specific time operations
      const newTimeMap = new Map();
      
      // Create a list of all unique time points where annotations change
      const timePoints = new Set();
      
      // Process each annotation once
      for (const ann of this.mainAnns) {
        const startKey = convertDecitimeToKey(ann.exact_time);
        const isSingular = InteractionService.isSingularInteraction(ann);
        const endTime = isSingular || ann.pause ? ann.exact_time + 0.2 : ann.must_click ? ann.exact_finish : ann.exact_finish + 0.1;
        const endKey = convertDecitimeToKey(Math.round(endTime * 10) / 10);
        
        // Add to timePoints set
        timePoints.add(startKey);
        timePoints.add(endKey);
        
        // Initialize start time entry if needed
        if (!newTimeMap.has(startKey)) {
          newTimeMap.set(startKey, { show: [], hide: [] });
        }
        newTimeMap.get(startKey).show.push(ann);
        
        // Initialize end time entry if needed
        if (!newTimeMap.has(endKey)) {
          newTimeMap.set(endKey, { show: [], hide: [] });
        }
        newTimeMap.get(endKey).hide.push(ann);
      }
      
      // Convert timePoints to sorted array
      this.allTimePoints = Array.from(timePoints).sort((a, b) => a - b);
      
      // Build directVisibilityMap - but only for time points where annotations change
      // Instead of creating for every 0.1s increment, we create only at annotation boundaries
      
      // Compute the active state for each time point
      let currentActive = new Map();
      
      for (const timePoint of this.allTimePoints) {
        const value = newTimeMap.get(timePoint);
        
        // Process shows - add to active
        if (value && value.show) {
          for (const ann of value.show) {
            currentActive.set(ann.id, ann);
          }
        }
        
        // Process hides - remove from active
        if (value && value.hide) {
          for (const ann of value.hide) {
            currentActive.delete(ann.id);
          }
        }
        
        // Store a snapshot of the currently active annotations at this time point
        this.directVisibilityMap.set(timePoint, new Map(currentActive));
      }
      
      // Convert Map to object for compatibility with existing code
      this.timeMap = Object.fromEntries(
        Array.from(newTimeMap.entries()).sort(([a], [b]) => a - b)
      );
      
      // Reset active annotations for current time
      this.resetActive(playerStore.decitime, "setMap");
      
      log.info(2,"🔎 AnnStore setMap", Math.round(performance.now() - totalStart), "ms");
    },
    
    resetActive(argTime, from=null) {
      const playerStore = PlayerStore()
      if (playerStore.branching) {
        log.info(2,"💟 AnnStore resetActive branching, skipping reset")
        this.active.clear()
        return
      }
      const time = argTime == undefined || argTime == null ? playerStore.decitime : argTime
      const start = performance.now();
      
      // Mark this as an intentional seek when called explicitly with a time
      if (argTime !== undefined && argTime !== null) {
        this.markIntentionalSeek();
      }
      
      this._resetActiveCore(time, start, from);
    },

    // Reset active state without marking as intentional seek (for thread blocking recovery)
    resetActiveForThreadBlocking(time) {
      const start = performance.now();
      this._resetActiveCore(time, start);
    },

    // Core reset active logic shared by both methods
    _resetActiveCore(time, start, from=null) {
      
      // Skip redundant resets for the same time
      // if (this.lastReset === time && time !== 0) {
      //   log.info(2,"💟 AnnStore resetActive, skipping reset", time);
      //   return;
      // }

      const playerStore = PlayerStore()
      const videoStore = VideoStore()
      if (!playerStore.mediaPlayer) {
        log.info(2,"💟 AnnStore resetActive, skipping reset because no media player", time, from);
        return;
      }

      const questionsAtResetTime = this.visibleMainAnns.filter(
        ann => ann.type_of === 'question' && ann.exact_time === time && !this.isDoneAt(time, ann.id)
      );
    
      if (questionsAtResetTime.length > 0) {
        this.setQuestionQueue(questionsAtResetTime);
        this.showNextQuestion();
        return;
      }
      
      log.info(2,"🔴🔴🔴 Reset Active -->" + time, playerStore.decitime, this.timeMap[convertDecitimeToKey(time)] ? this.timeMap[convertDecitimeToKey(time)].show.length : 0, from)
      this.lastReset = time;
      
      // Clear done interactions from other times
      for (const decitime of this.doneInteractions.keys()) {
        if (decitime !== time) {
          this.clearDoneAt(decitime);
        }
      }
      
      // Clear previous active interactions
      this.active.clear();
      
      // Find the nearest timePoint that is less than or equal to the current time
      const timeKey = convertDecitimeToKey(time);
      const nearestTimePoint = this.findNearestTimePoint(timeKey);
      
      if (nearestTimePoint !== null) {
        // Get the precomputed active state at this time point
        const activeAtNearestTime = this.directVisibilityMap.get(nearestTimePoint);
        
        if (activeAtNearestTime) {
          // Copy the precomputed state to our active map
          activeAtNearestTime.forEach((ann, id) => {
            // For singular interactions, only add if at exact time and not done
            if (InteractionService.isSingularInteraction(ann)) {

              if (time === ann.exact_time && !this.isDoneAt(time, ann.id)) {
                this.doShow(ann);
              }
            } else {

              // For other interactions, check if they should still be active at current time
              if (ann.exact_time <= time && time <= ann.exact_finish) {
                this.active.set(id, ann);
                if (ann.exact_time == time) {
                  log.info(2,"💟 AnnStore resetActive exactTime match, do show", ann.type_of, ann.exact_time, ann.exact_finish, this.isDoneAt(ann.exact_time, ann.id))
                  this.doShow(ann);
                }
              }
            }
          });
        }
        
        // If we're at the exact time point, still need to process any interactions at this exact time
        if (nearestTimePoint === timeKey) {
          // Process any show/hide interactions at this exact time
          const exactTimeValue = this.timeMap[timeKey];
          if (exactTimeValue) {
            // Process shows at exact time
            if (exactTimeValue.show) {
              for (const ann of exactTimeValue.show) {
                if (!ann.hidden) {
                  if (InteractionService.isSingularInteraction(ann)) {
                    if (!this.isDoneAt(time, ann.id)) {
                      this.doShow(ann);
                    }
                  } else {
                    this.doShow(ann);
                  }
                }
              }
            }
            
            // Process hides at exact time
            if (exactTimeValue.hide) {
              for (const ann of exactTimeValue.hide) {
                this.doHide(ann);
              }
            }
          }
          
          log.info(2,"Reset Active (exact match) finished in", performance.now() - start, "ms");
          return;
        }
        
        // Otherwise, process any changes between nearestTimePoint and current time
        const nextTimePoints = this.allTimePoints.filter(
          tp => tp > nearestTimePoint && tp <= timeKey
        );
        
        for (const tp of nextTimePoints) {
          const value = this.timeMap[tp];
          
          // Process shows
          if (value && value.show) {
            for (const ann of value.show) {
              if (!ann.hidden) {
                if (InteractionService.isSingularInteraction(ann)) {
                  if (time === ann.exact_time && !this.isDoneAt(time, ann.id)) {
                    this.doShow(ann);
                  }
                } else {
                  this.doShow(ann);
                }
              }
            }
          }
          
          // Process hides
          if (value && value.hide) {
            for (const ann of value.hide) {
              this.doHide(ann);
            }
          }
        }
      } else {
        // Fallback to traditional method if no nearest time point found
        // This should never happen unless there are no annotations
        
        // Find all interactions that should be visible at current time
        const timeKeys = Object.keys(this.timeMap)
          .filter(key => key <= timeKey)
          .sort((a, b) => a - b);
        
        // Process each time key sequentially
        for (const key of timeKeys) {
          const value = this.timeMap[key];
          
          // Process shows and hides
          if (value.show) {
            for (const ann of value.show) {
              if (!ann.hidden) {
                if (InteractionService.isSingularInteraction(ann)) {
                  if (time === ann.exact_time && !this.isDoneAt(time, ann.id)) {
                    log.info(2,"💟 AnnStore resetActive exactTime match SINGULAR, do show", ann.type_of, ann.exact_time, ann.exact_finish, this.isDoneAt(ann.exact_time, ann.id))
                    this.doShow(ann);
                  }
                } else {
                  this.doShow(ann);
                }
              }
            }
          }
          
          if (value.hide) {
            for (const ann of value.hide) {
              this.doHide(ann);
            }
          }
        }
      }
      
      // log.info(2,"Reset Active finished in", performance.now() - start, "ms");
    },
    
    // Helper method to find the nearest time point in allTimePoints
    findNearestTimePoint(targetTime) {
      if (!this.allTimePoints || this.allTimePoints.length === 0) return null;
      
      // Binary search to find the closest time point that's <= target time
      let left = 0;
      let right = this.allTimePoints.length - 1;
      let result = null;
      
      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const midTime = this.allTimePoints[mid];
        
        if (midTime <= targetTime) {
          // This is a potential result, but we need to find the largest such time
          result = midTime;
          left = mid + 1;
        } else {
          // Too large, look in left half
          right = mid - 1;
        }
      }
      
      return result;
    },

    // Helper method to find the nearest cached time point
    findNearestCachedTime(targetTime) {
      if (!this.timeCache || this.timeCache.size === 0) return null;

      // Get all cached times that are ≤ target time
      const cachedTimes = Array.from(this.timeCache.keys())
        .filter(time => time <= targetTime)
        .sort((a, b) => b - a); // Sort in descending order

      return cachedTimes.length > 0 ? cachedTimes[0] : null;
    },

    // Helper to process only a specific time range
    processTimeRange(startTime, endTime) {
      const startKey = convertDecitimeToKey(startTime);
      const endKey = convertDecitimeToKey(endTime);

      // Get only the keys in the range
      const keysToProcess = Object.keys(this.timeMap)
        .filter(key => key > startKey && key <= endKey)
        .sort((a, b) => a - b);

      // Process only these keys
      for (const key of keysToProcess) {
        const value = this.timeMap[key];

        // Process shows
        for (const ann of value.show) {
          if (!ann.hidden) {
            if (InteractionService.isSingularInteraction(ann)) {
              if (endTime === ann.exact_time && !this.isDoneAt(ann.exact_time, ann.id)) {
                this.doShow(ann);
              }
            } else {
              this.doShow(ann);
            }
          }
        }

        // Process hides
        for (const ann of value.hide) {
          this.doHide(ann);
        }
      }
    },


    // Cache the current state at a specific time point
    cacheCurrentState(time) {
      // log.info(2,"Cache Current State", time)
      // Limit the cache size to prevent memory issues
      if (this.timeCache.size > 300) {
        // Remove the oldest cache entry
        const oldestTime = Math.min(...this.timeCache.keys());
        this.timeCache.delete(oldestTime);
      }

      // Store a deep copy of the current active state
      this.timeCache.set(time, new Map(this.active));
    },

    // Add this to make the cache system work properly
    clearCache() {
      this.timeCache.clear();
    },

    updateActive(time) {
      const clickStore = ClickStore()
      const playerStore = PlayerStore()
      const videoStore = VideoStore()
      const timeKey = Number(time).toFixed(1)
      if (this.lastOwnerSwitchPromptTime && timeKey !== this.lastOwnerSwitchPromptTime) {
        this.ownerSwitchTimeLeft = true
      }

      if (playerStore.branching) {
        log.info(2,"💟 AnnStore resetActive branching, skipping reset")
        this.active.clear()
        return
      }
      
      // Skip if currently seeking
      if (playerStore.playerState.seeking) {
        log.info(2,"🔎 AnnStore skipping update active because seeking")
        return
      }
      
      // Check for missed updates (thread blocking detection)
      const EPSILON = 1e-10;
      const expectedGap = 0.1; // Expected 0.1s between updates
      
      if (this.lastUpdateTime && time > this.lastUpdateTime + expectedGap + EPSILON) {
        const gap = time - this.lastUpdateTime;
        const gapStartTime = this.lastUpdateTime;
        
        // Update lastUpdateTime immediately to prevent overlapping ranges
        this.lastUpdateTime = Number(time);
        
        // Only track as missed if it's not an intentional seek
        if (!this.isIntentionalSeek) {
          this.trackMissedUpdate(time, gapStartTime, 'thread_blocked');
          
          // For severe gaps (>2s), also reset active state as a safety measure
          // But don't mark this as an intentional seek since it's thread blocking recovery
          if (gap > 2.0) {
            log.info(2,"🔴 Large gap detected, forcing resetActive", gap);
            // Call resetActive without marking as intentional seek
            this.resetActiveForThreadBlocking(time);
          }
        } else {
          log.info(2,"🔄 Intentional seek detected, skipping missed update tracking", gap);
        }
      } else {
        this.lastUpdateTime = Number(time);
      }
      
      const leadCaptureStore = LeadCaptureStore()
      if (leadCaptureStore.checkTriggers(time)) {
        return
      }
      if (videoStore.video && videoStore.video.capture_payment_point > -1) {

        const paymentStore = PaymentStore()
        if (paymentStore.checkTriggers(time)) {
          playerStore.pause("AnnStore updateActive paymentStore checkTriggers")
          return
        }
      }
      // If there are any interactions marked as done at other times, clear them
      for (const decitime of this.doneInteractions.keys()) {
        if (decitime !== time) {
          this.clearDoneAt(decitime)
        }
      }
      if (this.timeMap[convertDecitimeToKey(time)]) {
        log.info(2, "TimeMapShow at time", time, this.timeMap[convertDecitimeToKey(time)].show)
      }
      const newInteractions = this.timeMap[convertDecitimeToKey(time)] && this.timeMap[convertDecitimeToKey(time)].show || []
      if (newInteractions.length > 0) {
        log.info(2,"💟 AnnStore updateActive newInteractions: ", newInteractions.map(ann => ann.type_of + ":" + (ann.content || ann.question_prompt) + " (" + ann.exact_time + " - " + ann.exact_finish + ")"))
      }
      if (this.lowerVolumePoints.includes(time)) {
        playerStore.lowerVolume()
      }
      // Hide expired interactions
      const removes = this.timeMap[convertDecitimeToKey(time)] ? this.timeMap[convertDecitimeToKey(time)].hide : []
      removes.forEach(ann => {
        this.doHide(ann)
      })

      // Check if we have any questions that haven't been done yet
      const questions = newInteractions.filter(ann =>
        ann.type_of === 'question' && !this.isDoneAt(ann.exact_time, ann.id) && !ann.hidden
      )
      if (questions.length > 0) {
        // Queue up the questions
        log.info(2,"💟 AnnStore updateActive, questions length:", questions.length)
        if (this.isDoneAt(questions[0].exact_time, questions[0].id)) {
          log.info(2,"💟 AnnStore updateActive, questions already done, skipping")
          return
        } else if (questions[0].id) { // For post editing questions, we don't want to show them
          log.info(2,"💟 AnnStore updateActive, questions not done, showing")
          this.setQuestionQueue(questions)
          // Show the first question
          this.showNextQuestion()
          // return
        }
      } else if (newInteractions.some(ann =>
        ann.type_of === 'drawing' && !this.isDoneAt(ann.exact_time, ann.id) && !clickStore.questionActive
      )) {
        const drawing = newInteractions.find(ann =>
          ann.type_of === 'drawing' && !this.isDoneAt(ann.exact_time, ann.id)
        )
        clickStore.showDrawing(drawing)
        this.markAsDone(drawing.exact_time, drawing.id)
        return
      }

      // Add new items from newInteractions
      newInteractions.forEach(newInteraction => {
        this.doShow(newInteraction)
      });
      this.cacheCurrentState(time)

      this.lastReset = time
    },

    // Show an interaction
    // ann_store.js - Updated doShow method
    doShow(ann) {
      log.info(2,"💟 AnnStore doShow", ann.type_of, ann.content + " (" + ann.exact_time + " - " + ann.exact_finish + ")")
      if (!ann.visible) {
        log.info(2,"💟 AnnStore doShow not visible", ann.type_of, ann.content + " (" + ann.exact_time + " - " + ann.exact_finish + ")")
        return
      }
      
      const playerStore = PlayerStore()
      const clickStore = ClickStore()
      const playbackStore = PlaybackStore()
      const videoStore = VideoStore()
      const audioStore = AudioStore()
      const clipStore = ClipStore()
      const router = useRouter()
      const leadCaptureStore = LeadCaptureStore()

      if (InteractionService.isSingularInteraction(ann)) {
        if (this.isDoneAt(ann.exact_time, ann.id)) {
          log.info(2,"💟 AnnStore doShow ALREADY done", ann.exact_time, ann.id)
          return
        }
      }

      const result = InteractionService.processShowInteraction(ann, {
        router: router,
        playerStore: playerStore,
        playbackStore: playbackStore,
        videoStore: videoStore,
        clickStore: clickStore,
        audioStore: audioStore,
        clipStore: clipStore,
        annStore: this
      })

      log.info(3, "💟 zAnnStore doShow result for ", ann.type_of, result)

      if (result.action) {
        this.markAsDone(ann.exact_time, ann.id)
      }
      if (result.message) {
        this.showOwnerMessage(result.message)
      }

      // Handle state updates
      if (result.shouldShow && !this.active.has(ann.id)) {
        log.info(2,"💟 AnnStore doShow adding to active", ann.id)
        this.active.set(ann.id, ann);
      }

      // log.info(2,"💟 AnnStore doShow result.action", result.action)
      // Handle follow-up actions
      // log.info(2,"💟 AnnStore doShow result", result)
      if (clickStore.questionActive) {
        let acceptableActions = ['reset_viewer', 'reset_interactions', 'setVariable']
        if (result.action &&!acceptableActions.includes(result.action)) {
          log.info(2,"💟 Question active, skipping " + ann.type_of + " (" + ann.exact_time + " - " + ann.exact_finish + ")", result.action)
          return
        }
      }
      switch (result.action) {
        case 'pauseUntilClick':
          if (ann.exact_time == playerStore.decitime) {
            playerStore.pause("AnnStore doShow pauseUntilClick")
            // Don't set pauseUntilClick if its a pause, nothing to be clicked
            if (ann.type_of != 'pause') {
              this.pausedInteraction = result.ann
            }
          } else {
            log.info(2,"💟 AnnStore doShow pauseUntilClick clearing", ann.exact_time, playerStore.decitime)
            clickStore.clearTimedPause()
          }
          break
        case 'startTimedPause':
          if (ann.exact_time == playerStore.decitime) {
            log.info(2,"💟 AnnStore doShow startTimedPause", ann.exact_time, playerStore.decitime)
            playerStore.pause("AnnStore doShow startTimedPause")
            clickStore.startTimedPause(result.timer)
          } else {
            log.info(2,"💟 AnnStore doShow startTimedPause clearing", ann.exact_time, playerStore.decitime)
            clickStore.clearTimedPause()
          }
          break
        case 'showMagicMenu':
          clickStore.showMagicMenu(result.ann)
          break
        case 'reset_viewer':
          playbackStore.resetViewer()
          break
        case 'reset_interactions':
          playbackStore.resetInteractions()
          break
        case 'back':
          log.info(2,"💟 AnnStore doShow back, refer_video is " + playbackStore.refer_video)
          VideoService.branchToVideo(playbackStore.refer_video, playbackStore.refer_time || 0, {
            router: router,
            player: playerStore,
            playback: playbackStore,
            video: videoStore,
            ann: this,
            leadCapture: leadCaptureStore
          })
          // playerStore.back()
          break
        case 'jump':
          playerStore.seek(result.value)
          break
        case 'setVariable':
          doVariableEvaluation(result.variable, result.value)
          break
        case 'chatGPT':
          log.info(2,"💟 AnnStore doShow chatGPT", result.value)
          clickStore.askChatGPT(videoStore.video, ann.click_value, ann.variable)
          break
        case 'videoBranch':
          log.info(2,"💟 AnnStore doShow videoBranch", result.token, ann.click_time)
          VideoService.branchToVideo(result.token, ann.click_time || 0, {
            router: router,
            player: playerStore,
            playback: playbackStore,
            video: videoStore,
            ann: this,
            leadCapture: leadCaptureStore
          })
          break
        case 'ownerSwitchPrompt':
          this.openOwnerSwitchDialog(result.ann)
          break
        case 'redirect':
          log.info(2,"💟 AnnStore doShow result action redirect", result.target)
          doRedirect(result.target)

          break
      }

      // log.info(2,"AnnStore post doShowLoop")

      // Mark as done for this time if it's a singular interaction
      if (InteractionService.isSingularInteraction(ann)) {
        this.markAsDone(ann.exact_time, ann.id)
      }

      // Handle any promises from async operations
      if (result.processPromise) {
        result.processPromise.catch(error => {
          log.info(1,"Error processing interaction:", error)
        })
      }
    },

    doHide(ann) {
      log.info(2,"💟  AnnStore doHide", ann.type_of + " (" + ann.exact_time + " - " + ann.exact_finish + ")")
      const playerStore = PlayerStore()
      // Check if must be clicked
      if (ann.must_click && !ann.clicked) {
        log.info(2,"Must click, time comparison: ", ann.exact_finish, playerStore.decitime, ann.exact_finish == playerStore.decitime)
        if (Math.abs(ann.exact_finish - playerStore.decitime) <= 0.2) {
          // Ensure the button is shown before pausing (in case we landed exactly at hide time)
          if (!this.active.has(ann.id)) {
            log.info(2,"💟  AnnStore doHide - showing must_click button before pausing")
            this.doShow(ann)
          }
          this.doMustClick(ann)
          return
        }
      }
      if (this.active.has(ann.id)) {
        // const activeAnn = this.active.get(ann.id);
        // if (activeAnn) {
        //     activeAnn.isActive = false;
        // }
        this.active.delete(ann.id);
      }
    },

    doPauseForAnn(ann) {
      const playerStore = PlayerStore()
      const clickStore = ClickStore()
      log.info(2,"💟  AnnStore doPauseForAnn", ann, "Time is: ", ann.timer)
      playerStore.pause("AnnStore doPauseForAnn")
      if (ann.timer == -1) {
        log.info(2,"💟  AnnStore doPauseForAnn pausing until click")
        this.pausedInteraction = ann
      } else {
        log.info(2,"💟  AnnStore doPauseForAnn pausing for", ann.timer, "seconds")
        clickStore.startTimedPause(ann.timer)
      }
    },

    doPauseUntilClick(ann) {
      const playerStore = PlayerStore()
      log.info(2,"💟  AnnStore doPauseUntilClick", ann)
      this.pausedInteraction = ann
      playerStore.pause("AnnStore doPauseUntilClick")
    },

    doMustClick(ann) {
      const playerStore = PlayerStore()
      log.info(2,"💟  AnnStore doMustClick", ann, this.mustClickInteractions)
      if (!this.mustClickInteractions.some(interaction => interaction.id === ann.id)) {
        this.mustClickInteractions.push(ann)
      }
      playerStore.pause("AnnStore doMustClick")

    },

    resetMustClickInteractions() {
      log.info(2,"💟  AnnStore resetMustClickInteractions initial", this.mustClickInteractions.length)
      this.mustClickInteractions = this.mustClickInteractions.filter(interaction => !interaction.clicked)
      log.info(2,"💟  AnnStore resetMustClickInteractions final", this.mustClickInteractions.length)
    },

    setExistingInteractions(interactions) {
      log.info(2,"🔎 AnnStore setExistingInteractions", interactions)
      interactions.forEach(int => {
        let ann = this.findAnnotationById(int.parent_id)
        if (ann) {
          ann.clicked = true
        }
      })
    },
    resetAnnotationState() {
      this.userInteractions = []
      this.anns.forEach(ann => {
        ann.clicked = false
      })
    },
    // Hovering NOT on the marker bar
    startHover(ann) {
      this.hoverAnn = ann
    },

    endHover() {
      this.hoverAnn = null
    },

    // Hovering on the marker bar
    startMarkerHover(ann) {
      this.markerAnn = ann
      this.hoverAnn = ann
      // Preload clip/audio on hover for better responsiveness
    },

    endMarkerHover() {
      this.markerAnn = null
      this.hoverAnn = null
    },
    resetState() {
      // log.info(2,"🟠 🟠 🟠 AnnStore resetState 🟠 🟠 🟠")
      this.anns = []
      this.pausedInteraction = null
      this.mustClickInteractions = []
      this.timeMap = {}
      this.isEditing = false
      this.doneInteractions = new Map()
      this.editAnn = null
      this.ogAnn = null
      this.active.clear();
      this.ownerSwitchDialogVisible = false
      this.ownerSwitchAnn = null
      this.lastOwnerSwitchPromptTime = null
      this.ownerSwitchTimeLeft = true
      this.clearMissedUpdates(); // Clear missed updates when resetting
      this.clearIntentionalSeek(); // Clear seek flags
    },

    async destroy(id) {
      return this.Api.destroy(`/anns/${id}`).then(response => {
        this.errors = {};
      }).catch(error => {
        this.errors = error.response.data.errors;
      })
    },

    getVariableOptions(includeOrg = true) {
      const userStore = UserStore()
      const videoStore = VideoStore()
      const uniqueVariables = new Map()

      const addVariable = (variable) => {
        const cleaned = (variable || '').toString().trim()
        if (!cleaned) return
        const key = cleaned.toUpperCase()
        if (!uniqueVariables.has(key)) {
          uniqueVariables.set(key, cleaned)
        }
      }

      const addVariables = (variables) => {
        (variables || []).forEach(addVariable)
      }

      addVariables(['name', 'pScore', 'cScore'])

      if (includeOrg) {
        try {
          addVariables(Object.keys(userStore.organization?.variables || {}))
        } catch (e) {
          log.info(2,"Error parsing org variables", e)
        }
      }

      try {
        addVariables(Object.keys(videoStore.video?.variables || {}))
      } catch (e) {
        log.info(2,"Error parsing video variables", e)
      }

      // Include any tokens used directly in top-level video strings.
      addVariables(
        extractTemplateVariablesFromValue([
          videoStore.video?.title,
          videoStore.video?.message,
          videoStore.video?.end_message,
          videoStore.video?.end_text,
          videoStore.video?.end_value,
        ])
      )

      // Include variables explicitly set by interactions and any {{TOKEN}} usage.
      this.anns.forEach(ann => {
        addVariable(ann.variable)
        addVariables(
          extractTemplateVariablesFromValue([
            ann.content,
            ann.question_prompt,
            ann.caption,
            ann.click_action,
            ann.click_value,
            ann.question_answers,
            ann.question_correct_value,
            ann.question_incorrect_value,
          ])
        )
      })

      return [...uniqueVariables.values()]
    },
    // List of non-default variables for use within video
    getLimitedVariableOptions() {
      const results = this.getVariableOptions(false)
      return results.filter(result => result !== 'pScore' && result !== 'cScore')
    },
    addEndAnn(type) {
      this.addAnn(type)
      this.editAnn.tray_type = 'ctaTray'
      this.setMenuAndEndPositions()
      log.info(2,"🔎 AnnStore addEndAnn", this.editAnn)
    },
    addMenuAnn(type) {
      this.addAnn(type)
      this.editAnn.tray_type = 'resourceTray'
      this.setMenuAndEndPositions()

      log.info(2,"🔎 AnnStore addMenuAnn", this.editAnn)
    },

    setMenuAndEndPositions() {
      const videoStore = VideoStore()
      let design = videoStore.video.design

      if (this.editAnn.tray_type == 'resourceTray' && design.magic_menu.width == 100) {
        return
      } else if (this.editAnn.tray_type == 'ctaTray' && design.end_menu.width == 100) {
        return
      }

      this.editAnn.pos_left = 10
      this.editAnn.pos_top = 10

      if (this.editAnn.tray_type == 'resourceTray') {
        this.editAnn.pos_width = Math.min((design.end_menu.width/100) * 50, 25)
        // this.editAnn.pos_height = Math.min((design.magic_menu.height/100) * 50, 25)
      } else if (this.editAnn.tray_type == 'ctaTray') {
        this.editAnn.pos_width = Math.min((design.end_menu.width/100) * 50, 25)
        // this.editAnn.pos_height = Math.min((design.magic_menu.height/100) * 50, 25)
      }
      if (ann.type_of == 'hotspot') {
        this.editAnn.pos_height = this.editAnn.pos_width
      }
    },

    showNextQuestion() {
      const playerStore = PlayerStore()
      const videoStore = VideoStore()
      if (!playerStore.mediaPlayer) {
        log.info(2,"💟 AnnStore showNextQuestion, skipping because no media player", playerStore.decitime);
        return;
      }
      log.info(2,"💟 AnnStore showNextQuestion, length:", this.activeQuestionQueue.length)
      if (this.activeQuestionQueue.length === 0) {
        log.info(2,"💟 AnnStore showNextQuestion no more questions")
        // No more questions, resume normal playback
        const playerStore = PlayerStore()
        const clickStore = ClickStore()
        clickStore.finishQuestions()
        // this.resetActive(playerStore.decitime)
        setTimeout(() => {
          playerStore.resetVolume()
          playerStore.play("annStore showNextQuestion")
        }, 10)
        return
      }

      const nextQuestion = this.activeQuestionQueue[0]
      log.info(2,"💟 AnnStore showNextQuestion nextQuestion", nextQuestion.question_prompt, nextQuestion.visible)

      // Check if this question should be shown based on conditions
      // if (nextQuestion.is_conditional && !this.evaluateCondition(nextQuestion)) {
      if (!nextQuestion.visible) {
        log.info(2,"💟 AnnStore showNextQuestion question not visible, skipping")
        // Skip this question if conditions aren't met
        this.markAsDone(nextQuestion.exact_time, nextQuestion.id) // Mark as done so we don't show it again
        this.activeQuestionQueue.shift() // Remove from queue
        this.showNextQuestion() // Process next question
        return
      }

      // Show the question
      const clickStore = ClickStore()
      clickStore.showQuestion(nextQuestion)
      this.markAsDone(nextQuestion.exact_time, nextQuestion.id)
    },

    tryFinishQuestion() {
      const clickStore = ClickStore()
      if (clickStore.isForceCorrect && !clickStore.questionCorrectness.isCorrect) {
        log.info(2,"💟 AnnStore tryFinishQuestion force correct and incorrect, skipping")
        return
      } else {
        log.info(2,"💟 AnnStore tryFinishQuestion, finishing")
        this.finishCurrentQuestion()
      }
    },

    finishCurrentQuestion() {
      log.info(2,"💟 AnnStore finishCurrentQuestion, current length:", this.activeQuestionQueue.length)
      const playbackStore = PlaybackStore()
      playbackStore.setAutomaticVariables()
      this.handleVariableChange()
      log.info(2,"💟 AnnStore tryFinishQuestion conditional statuses set and variables set, new length:", this.activeQuestionQueue.length)
      // const clickStore = ClickStore()
      // clickStore.finishTimedPause()
      // Remove the current question from the queue
      this.activeQuestionQueue.shift()

      // Show the next question or resume playback
      this.showNextQuestion()
    },

    // Called when a question is answered
    handleQuestionAnswer(questionId, answer) {
      log.info(2,"💟 AnnStore handleQuestionAnswer", questionId, answer)
      // Store the answer for potential conditional logic
      // Process any question-specific logic
      // Then move to next question
      this.finishCurrentQuestion()
    },
    clearQuestionQueue() {
      // log.info(2,"🔎 AnnStore clearQuestionQueue")
      this.activeQuestionQueue = []
      this.originalQuestionQueue = []
    },
    setQuestionQueue(queue) {
      const playerStore = PlayerStore()
      if (!playerStore.started) {
        log.info(2,"🔎 AnnStore setQuestionQueue return not started")
        return
      }
      log.info(2,"🔎 AnnStore setQuestionQueue", queue)
      this.activeQuestionQueue = queue
      this.originalQuestionQueue = queue.filter(q => q.visible).map(q => q.id)
    },
    // Add these methods to the actions:
    async updateImageDimensions(asset) {
      log.info(2,"🔎 AnnStore updateImageDimensions", asset)
      this.imageLoading = true
      this.editAnn.content = asset
      try {
        await this.setEditingImageAspectRatio()
        // Force a recalculation by temporarily removing and re-adding the editAnn
        const tempAnn = { ...this.editAnn }
        this.editAnn = {}
        await nextTick()
        this.editAnn = tempAnn
      } catch (error) {
        log.info(1,'Failed to update image dimensions:', error)
      } finally {
        this.imageLoading = false
      }
    },

    showOwnerMessage(message) {
      if (this.ownerMessage) {
        return
      }
      this.ownerMessage = message
      setTimeout(() => {
        this.ownerMessage = null
      }, 2000)
    },
    ownerSwitchTimeKey(ann) {
      const time = Number(ann?.exact_time)
      if (Number.isNaN(time)) {
        return null
      }
      return time.toFixed(1)
    },
    shouldPromptOwnerSwitch(ann) {
      const timeKey = this.ownerSwitchTimeKey(ann)
      if (!timeKey) {
        return false
      }
      if (this.lastOwnerSwitchPromptTime === timeKey && !this.ownerSwitchTimeLeft) {
        return false
      }
      return true
    },
    openOwnerSwitchDialog(ann) {
      const timeKey = this.ownerSwitchTimeKey(ann)
      if (timeKey) {
        this.lastOwnerSwitchPromptTime = timeKey
        this.ownerSwitchTimeLeft = false
      }
      this.ownerSwitchAnn = ann
      this.ownerSwitchDialogVisible = true
      const playerStore = PlayerStore()
      playerStore.pause("AnnStore owner switch dialog")
    },
    closeOwnerSwitchDialog() {
      this.ownerSwitchDialogVisible = false
      this.ownerSwitchAnn = null
    },
    ownerSwitchContinue() {
      this.closeOwnerSwitchDialog()
      const playerStore = PlayerStore()
      playerStore.play("AnnStore owner switch continue")
    },
    ownerSwitchBranch() {
      const ann = this.ownerSwitchAnn
      this.closeOwnerSwitchDialog()
      if (!ann?.click_value) {
        return
      }
      const token = ann.click_value.split("---")[1]
      if (!token) {
        return
      }
      const playerStore = PlayerStore()
      const playbackStore = PlaybackStore()
      const videoStore = VideoStore()
      const router = useRouter()
      const leadCaptureStore = LeadCaptureStore()
      VideoService.branchToVideo(token, ann.click_time || 0, {
        router: router,
        player: playerStore,
        playback: playbackStore,
        video: videoStore,
        ann: this,
        leadCapture: leadCaptureStore
      })
    },
    annotationsAtTime(time) {
      return this.mainAnns.filter(ann => ann.exact_time == time)
    },

    getActive() {
      return Array.from(this.active.keys())
    },


  },
  getters: {
    hasComments: (state) => {
      return state.anns.some(ann => ann.type_of == 'comment')
    },
    hasAudios: (state) => {
      return state.anns.some(ann => ann.type_of == 'audio')
    },
    dynamicAnns: (state) => {
      return state.anns.filter(ann => ann.dynamic)
    },
    pausePoints: (state) => {
      return state.anns.filter(ann => ann.pause || ['question', 'pause', 'video', 'audio'].includes(ann.type_of)).map(ann => ann.exact_time)
    },
    lowerVolumePoints: (state) => {
      // Calculate the times that we want to lower volume (1 second before a pausing interaction)
      return state.anns
        .filter(ann => ['question', 'video', 'audio'].includes(ann.type_of) || ann.pause)
        .map(ann => Math.max(0, Number((Number(ann.exact_time || 0) - 1).toFixed(1))))
      // We don't want to lower audio if there is an audio or video clip playing within 1 second of the pause time
      // let clipPoints = state.anns.filter(ann => ann.type_of == 'audio' || ann.type_of == 'video').map(ann => ann.exact_time)
      
      // // Check all pause times, see if there is a clip in the 1 second before it, remove from pause times if so
      // pauseTimes.forEach(time => {
      //   if (clipPoints.some(point => Math.abs(point - time) < 1)) {
      //     log.info(2,"🔎 AnnStore lowerVolumePoints removing pause time at", time, "because of clip")
      //     pauseTimes = pauseTimes.filter(t => t !== time)
      //   }
      // })
      // log.info(2,"🔎 AnnStore lowerVolumePoints", pauseTimes)
      // return pauseTimes
    },
    allNeeds: (state) => {
      return {
        type_of: state.editAnn && state.editAnn.type_of,
        isQuestion: state.editAnn && state.editAnn.type_of == 'question',
        isDrawing: state.editAnn && state.editAnn.type_of == 'drawing',
        isImage: state.editAnn && state.editAnn.type_of == 'image',
        isJump: state.editAnn && state.editAnn.type_of == 'jump',
        isRedirect: state.editAnn && state.editAnn.type_of == 'redirect',
        isPause: state.editAnn && state.editAnn.type_of == 'pause',
        isSwitch: state.editAnn && state.editAnn.type_of == 'switch',
        isVariable: state.editAnn && state.editAnn.type_of == 'variable',
        isResourceTray: state.editAnn && state.editAnn.tray_type == 'resourceTray',
        isEndCta: state.editAnn && state.editAnn.tray_type == 'ctaTray',
        isResetViewer: state.editAnn && state.editAnn.type_of == 'reset_viewer',
        isResetInteractions: state.editAnn && state.editAnn.type_of == 'reset_interactions',
        isChatGPT: state.editAnn && state.editAnn.type_of == 'chatGPT',
        needsPauseOptions: state.needsPauseOptions,
        needsClickAction: state.needsClickAction,
        needsClickValue: state.needsClickValue,
        needsHideTime: state.needsHideTime,
        needsPlacement: state.needsPlacement,
        needsStyling: state.needsStyling,
        needsSettings: state.needsSettings,
        isNavigation: state.isNavigation,
        needsConditional: state.needsConditional,
        needsContent: state.needsContent,
        needsVariables: state.needsVariables,
        needsTiming: state.needsTiming,
      }
    },
    questionsWithCorrectness: (state) => {
      return state.anns.filter(ann => ann.type_of == 'question' && (ann.question_single_correct?.length || ann.question_multi_correct?.length) && !ann.hidden)
    },
    mainAnns: (state) => {
      return state.anns.filter(ann => !ann.tray_type).sort((a, b) => {
        const timeDiff = a.exact_time - b.exact_time
        if (timeDiff !== 0) return timeDiff
        // Only compare created_at if times are equal
        return a.created_at_integer - b.created_at_integer
      })
    },
    visibleMainAnns: (state) => {
      return state.mainAnns.filter(ann => !ann.hidden && ann.visible)
    },
    menuAnns: (state) => {
      return state.anns.filter(ann => ann.tray_type == 'resourceTray').sort((a, b) => {
        const timeDiff = a.exact_time - b.exact_time
        if (timeDiff !== 0) return timeDiff
        
        // Only compare created_at if times are equal
        return a.created_at_integer - b.created_at_integer
      })
    },
    visibleMenuAnns: (state) => {
      return state.menuAnns.filter(ann => !ann.hidden && ann.visible)
    },
    endAnns: (state) => {
      return state.anns.filter(ann => ann.tray_type == 'ctaTray').sort((a, b) => {
        const timeDiff = a.exact_time - b.exact_time
        if (timeDiff !== 0) return timeDiff
        
        // Only compare created_at if times are equal
        return a.created_at_integer - b.created_at_integer
      })
    },
    visibleEndAnns: (state) => {
      return state.endAnns.filter(ann => !ann.hidden && ann.visible)
    },
    canUseCorrectness: (state) => {
      return state.isQuestion && ['multiple', 'free', 'select', 'all', 'date', 'number'].includes(state.editAnn.question_type)
    },
    canMapHubspot: (state) => {
      return state.isQuestion && ['multiple', 'free', 'select', 'all', 'date', 'number', 'rating', 'poll'].includes(state.editAnn.question_type)
    },
    canMapClickfunnels: (state) => {
      return state.isQuestion && ['multiple', 'free', 'select', 'all', 'date', 'number', 'rating', 'poll'].includes(state.editAnn.question_type)
    },
    canUseMultiCorrect: (state) => {
      return state.isQuestion && ['free', 'date', 'number'].includes(state.editAnn.question_type)
    },
    canSetIndividualVariables: (state) => {
      return state.isQuestion && ['multiple', 'all', 'select'].includes(state.editAnn.question_type)
    },
    canUseRangeCorrect: (state) => {
      return state.isQuestion && ['number'].includes(state.editAnn.question_type)
    },
    // Hide pause controls for videos, audios, questions, navigations
    needsPauseOptions: (state) => {
      return state.editAnn && state.needsPlacement && !state.isClip && !state.isAudio && !state.isDrawing && !InteractionService.isSingularInteraction(state.editAnn)
    },
    needsAnswers: (state) => {
      log.info(2,"🔎 AnnStore needsAnswers", state.editAnn && state.editAnn.question_type)
      return state.isQuestion && ['multiple', 'all', 'select', 'likert', 'poll'].includes(state.editAnn.question_type)
    },
    showAnswersPanel: (state) => {
      return state.isQuestion && !['rating', 'likert', 'video', 'image', 'draw', 'voice'].includes(state.editAnn.question_type)
    },
    needsClickAction: (state) => {
      return state.editAnn && !state.isClip && !state.isAudio && !state.isDrawing && !InteractionService.isSingularInteraction(state.editAnn)
    },
    needsClickValue: (state) => {
      return (state.needsClickAction || state.isSwitch) && ['none', 'back', 'track', 'resourceTray', 'openai', 'genie'].indexOf(state.editAnn.click_action) === -1
    },
    needsHideTime: (state) => {
      return state.editAnn && state.needsPlacement && !state.editAnn.pause && state.needsClickAction && !InteractionService.isSingularInteraction(state.editAnn)
    },
    needsPlacement: (state) => {
      return state.editAnn && !state.isNavigation && !state.isQuestion && !state.isDrawing && !state.isAudio
    },
    needsStyling: (state) => {
      return state.editAnn && state.needsPlacement && !state.isNavigation && !state.isDrawing && !InteractionService.isSingularInteraction(state.editAnn)
    },
    needsSettings: (state) => {
      return state.editAnn && !state.isNavigation && !state.isDrawing && !InteractionService.isSingularInteraction(state.editAnn)
    },
    isNavigation: (state) => {
      return state.editAnn && ['jump', 'pause', 'redirect', 'switch', 'variable', 'resourceTray', 'reset_viewer', 'reset_interactions', 'chatGPT', 'back'].indexOf(state.editAnn.type_of) !== -1
    },
    isQuestion: (state) => {
      return state.editAnn && state.editAnn.type_of == 'question'
    },
    isImage: (state) => {
      return state.editAnn && state.editAnn.type_of == 'image'
    },
    isDrawing: (state) => {
      return state.editAnn && state.editAnn.type_of == 'drawing'
    },
    isHotspot: (state) => {
      return state.editAnn && state.editAnn.type_of == 'hotspot'
    },
    isText: (state) => {
      return state.editAnn && state.editAnn.type_of == 'text'
    },
    isButton: (state) => {
      return state.editAnn && state.editAnn.type_of == 'button'
    },
    isClip: (state) => {
      return state.editAnn && state.editAnn.type_of == 'clip'
    },
    isAudio: (state) => {
      return state.editAnn && state.editAnn.type_of == 'audio'
    },
    isSwitch: (state) => {
      return state.editAnn && state.editAnn.type_of == 'switch'
    },
    needsConditional: (state) => {
      return !state.isDrawing
    },
    needsContent: (state) => {
      return state.editAnn && (state.needsPlacement || state.isAudio) && !state.isDrawing && !state.isNavigation
    },
    needsVariables: (state) => {
      return state.editAnn && state.needsPlacement && !state.isNavigation && !state.isDrawing && !InteractionService.isSingularInteraction(state.editAnn)
    },
    needsTiming: (state) => {
      return state.editAnn && !state.editAnn.tray_type
    },
    needsFirstConditionalValue: (state) => {
      return state.editAnn && state.editAnn.is_conditional && !['exists', 'notexists'].includes(state.editAnn.conditional_assertion)
    },
    needsSecondConditionalValue: (state) => {
      return state.editAnn && state.editAnn.is_conditional_2 && !['exists', 'notexists'].includes(state.editAnn.conditional_assertion_2)
    },
    layerAnns: (state) => {
      return state.anns.filter(ann => !ann.tray_type)
    },
    validateQuestion: (state) => {
      let a = state.editAnn
      if (!a) return { valid: true, message: '', element: null }
      let result = { valid: true, message: '', element: null }
      if (!a.question_prompt) {
        return { valid: false, message: 'common.question_prompt_required', element: 'question-prompt-input' }
      } 
      if (state.needsAnswers ) {
        // Items not needing an action
        let actions = ['none', 'back', 'track', 'resourceTray', 'openai', 'genie', 'reply']
        a.question_answers?.forEach((answer, index) => {
          if (result.valid && ((!actions.includes(answer.click_action) && !isNotEmpty(answer.click_value)) || !isNotEmpty(answer.text))) {
            log.info(2,"🔎 AnnStore validateQuestion answer for index" + index, answer)
            result = { valid: false, message: 'common.question_answer_required', element: 'question-answer-' + index }
          }
        })
        if (!result.valid) {
          return result
        }
      }
      
      if (a.is_conditional) {
        if (!a.conditional_variable) {
          return { valid: false, message: 'common.conditional_variable_required', element: 'edit-ann-conditional-variable' }
        } else if (!a.conditional_assertion) {
          return { valid: false, message: 'common.conditional_assertion_required', element: 'edit-ann-conditional-operator' }
        } else if (state.needsFirstConditionalValue && !a.conditional_value) {
          return { valid: false, message: 'common.conditional_value_required', element: 'edit-ann-conditional-value' }
        }
      } 

      if (a.is_conditional_2) {
        if (!a.conditional_variable_2) {
          return { valid: false, message: 'common.conditional_variable_required', element: 'edit-ann-conditional-variable-2' }
        } else if (!a.conditional_assertion_2) {
          return { valid: false, message: 'common.conditional_assertion_required', element: 'edit-ann-conditional-operator-2' }
        } else if (state.needsSecondConditionalValue && !a.conditional_value_2) {
          return { valid: false, message: 'common.conditional_value_required', element: 'edit-ann-conditional-value-2' }
        }
      } 
      return result
    },
    validateAnnotation: (state) => {
      let a = state.editAnn
      if (!a) return { valid: true, message: '', element: null }
      let result = { valid: true, message: '', element: null }
      if (state.needsContent && a.type_of != 'hotspot' && !a.content) {
        return { valid: false, message: 'common.content_required', element: 'edit-ann-content' }
      }
      if (state.needsTiming && a.exact_time > a.exact_finish) {
        return { valid: false, message: 'common.show_time_must_be_before_hide_time', element: 'edit-ann-exact-time' }
      }
      if (!a.click_action && state.needsClickAction) {
        return { valid: false, message: 'common.click_action_required', element: 'edit-ann-click-action' }
      } 
      if ((!a.click_value && a.click_value !== 0) && state.needsClickValue) {
        return { valid: false, message: 'common.click_value_required', element: 'edit-ann-click-value' }
      } 
      if (a.click_action == 'link' && !isValidURL(a.click_value)) {
        return { valid: false, message: 'common.valid_url_required', element: 'edit-ann-click-value' }
      }
      if (a.is_conditional) {
        if (!a.conditional_variable) {
          return { valid: false, message: 'common.conditional_variable_required', element: 'edit-ann-conditional-variable' }
        } else if (!a.conditional_assertion) {
          return { valid: false, message: 'common.conditional_assertion_required', element: 'edit-ann-conditional-operator' }
        } else if (state.needsFirstConditionalValue && !a.conditional_value) {
          return { valid: false, message: 'common.conditional_value_required', element: 'edit-ann-conditional-value' }
        }
      } 

      if (a.is_conditional_2) {
        if (!a.conditional_variable_2) {
          return { valid: false, message: 'common.conditional_variable_required', element: 'edit-ann-conditional-variable-2' }
        } else if (!a.conditional_assertion_2) {
          return { valid: false, message: 'common.conditional_assertion_required', element: 'edit-ann-conditional-operator-2' }
        } else if (state.needsSecondConditionalValue && !a.conditional_value_2) {
          return { valid: false, message: 'common.conditional_value_required', element: 'edit-ann-conditional-value-2' }
        }
      } 

      if (a.variable && !a.value && !['question', 'chatGPT'].includes(a.type_of)) {
        return { valid: false, message: 'common.variable_value_required', element: 'edit-ann-variable-value' }
      }

      if (a.value && !a.variable) {
        return { valid: false, message: 'common.variable_required', element: 'edit-ann-variable' }
      }
      log.info(2,"🔎 AnnStore validateAnnotation", result)
      return result
    },
    validateNavigation: (state) => {
      return { valid: true, message: '', element: null }
    },
    currentScore: (state) => {
      // log.info(2,"🔎 AnnStore currentScore")
      // Get all questions with correctness
      let questions = state.anns.filter(ann => ann.type_of == 'question' && (ann.question_single_correct?.length || ann.question_multi_correct?.length))
      const playbackStore = PlaybackStore()
      let used = []
      let correctCount = 0
      playbackStore.userInteractions.reverse().forEach(interaction => {
        // log.info(2,"Playback UserInteraction", JSON.parse(JSON.stringify(interaction)))
        let ann = questions.find(q => q.id == interaction.parent_id)
        if (ann && !used.includes(ann.id)) {
          used.push(ann.id)
          if (interaction.correct) {
            correctCount++
          }
        }
      })
      if (correctCount == 0) {
        return 0
      }
      let score = Math.round((correctCount / questions.length) * 100)
      log.info(2,"🔎 AnnStore currentScore", score)
      return score
    }
  }
})
